import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Snackbar, Alert, TextField, Typography, Box, Grid, Paper, Button, Chip, Stack, Divider,
} from '@mui/material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getOtrosById, updateOtros, deleteOtros, getPacienteInformeById } from '../api/otrosApi';

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 2,
  borderRadius: 3,
  border: '1px solid #dbe7f3',
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const fieldSx = (isEditing) => ({
  '& .MuiOutlinedInput-notchedOutline': { border: isEditing ? undefined : 'none' },
});

const OtrosViewer = ({ idTipo, paciente }) => {
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [lawtonBrody, setLawtonBrody] = useState(0);
  const [moca, setMoca] = useState(0);
  const [codigoInforme, setCodigoInforme] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [openGood, setOpenGood] = useState(false);
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    if (!paciente) return;

    getPacienteInformeById(paciente)
      .then(pacienteData => {
        setCodigo(pacienteData.codHistorico);
        setNombre(pacienteData.firstname);
        setApellidos(pacienteData.lastname);
      })
      .catch(error => console.error('Error fetching patient data:', error));

    getOtrosById(idTipo)
      .then(otrosData => {
        const d = new Date(otrosData.createdAt);
        const fecha = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
        setCodigoInforme(otrosData.codigo);
        setCreatedAt(fecha);
        setLawtonBrody(otrosData.lawtonBrody);
        setMoca(otrosData.moca);
      })
      .catch(error => console.error('Error fetching otros data:', error));
  }, [idTipo, paciente]);

  const handleEdit = () => {
    setOriginalValues({ lawtonBrody, moca });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setLawtonBrody(originalValues.lawtonBrody);
    setMoca(originalValues.moca);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateOtros(codigoInforme, {
      lawtonBrody: parseFloat(lawtonBrody),
      moca: parseFloat(moca),
    })
      .then(() => {
        setOpenGood(true);
        setIsEditing(false);
      })
      .catch(error => console.error('Error saving changes:', error));
  };

  const handleDelete = () => {
    if (!window.confirm('¿Está seguro de que desea eliminar este informe?')) return;
    deleteOtros(codigoInforme)
      .then(() => { navigate('/home'); })
      .catch(error => console.error('Error deleting the report:', error));
  };

  const handleExportPDF = () => {
    setIsEditing(false);
    const input = document.getElementById('pdf-content');
    html2canvas(input, { scale: 2, backgroundColor: null, useCORS: true, ignoreElements: (el) => el.tagName === 'BUTTON' })
      .then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (pdf.getImageProperties(imgData).height * pdfWidth) / pdf.getImageProperties(imgData).width;
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`valoracion_cognitiva_funcional_${nombre}${apellidos}_${createdAt}.pdf`);
      })
      .catch(error => console.error('Error exporting PDF:', error));
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ maxWidth: 900, width: '100%' }}>

        {/* Hero */}
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            borderRadius: 4,
            border: '1px solid #dbe7f3',
            background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
            boxShadow: '0 20px 45px rgba(15,23,42,0.08)',
          }}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography variant="overline" sx={{ color: '#1d4ed8', letterSpacing: 1.4 }}>
                  Valoración cognitiva y funcional
                </Typography>
                <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 600, mb: 0.5 }}>
                  Cuestionario Otros
                </Typography>
                <Typography sx={{ color: '#475569' }}>
                  Evaluación de Lawton y Brody y MoCA del paciente.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Fecha ${createdAt || '-'}`} variant="outlined" sx={{ borderColor: '#bfdbfe', color: '#1e3a8a' }} />
                <Chip label={isEditing ? 'Modo edición' : 'Solo lectura'} color={isEditing ? 'warning' : 'default'} />
              </Stack>
            </Stack>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Paciente</Typography>
                <Typography sx={{ color: '#0f172a', fontWeight: 600 }}>
                  {nombre || apellidos ? `${nombre} ${apellidos}` : 'Sin datos'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Código paciente</Typography>
                <Typography sx={{ color: '#0f172a', fontWeight: 600 }}>{codigo || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>Código informe</Typography>
                <Typography sx={{ color: '#0f172a', fontWeight: 600 }}>{codigoInforme || '-'}</Typography>
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        {/* Contenido */}
        <Box id="pdf-content">
          <Paper elevation={0} sx={sectionPaperSx}>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, mb: 2 }}>
              Resultados
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="LAWTON Y BRODY (puntos)"
                  variant="outlined"
                  fullWidth
                  value={lawtonBrody}
                  onChange={(e) => setLawtonBrody(e.target.value)}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx(isEditing)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="MOCA (puntos)"
                  variant="outlined"
                  fullWidth
                  value={moca}
                  onChange={(e) => setMoca(e.target.value)}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx(isEditing)}
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Acciones */}
        <Paper
          sx={{
            p: 2,
            borderRadius: 4,
            border: '1px solid #dbe7f3',
            backgroundColor: 'rgba(255,255,255,0.92)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
            {isEditing ? (
              <>
                <Button variant="outlined" onClick={handleCancel} sx={{ borderRadius: 2.5 }}>Cancelar</Button>
                <Button variant="contained" color="primary" onClick={handleSave} sx={{ borderRadius: 2.5 }}>Guardar cambios</Button>
                <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2.5 }}>Eliminar</Button>
              </>
            ) : (
              <>
                <Button variant="outlined" color="primary" onClick={handleEdit} sx={{ borderRadius: 2.5 }}>Editar</Button>
                <Button variant="contained" color="secondary" onClick={handleExportPDF} sx={{ borderRadius: 2.5 }}>Exportar PDF</Button>
                <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 2.5 }}>Eliminar</Button>
              </>
            )}
          </Stack>
        </Paper>

        <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
          <Alert severity="success" onClose={() => setOpenGood(false)}>¡Informe actualizado!</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default OtrosViewer;
